import Dialog from 'components/ReusableComponents/Dialog'
import PropTypes from 'prop-types'
import { useEffect, useState } from 'react'

import styles from './SavibookPreview.module.scss'
import { getSavibookPreviewCopy } from './savibookPreviewCopy'

const HERO_IMAGE = `${process.env.PUBLIC_URL}/ota-buster-hero.jpg`

const PREVIEW_SCREENS = {
  form: 'form',
  success: 'success'
}

function normalizeLanding(landing) {
  if (!landing) {
    return null
  }

  return {
    header: landing.header ?? landing.Header,
    body: landing.body ?? landing.Body,
    cta: landing.cta ?? landing.Cta,
    successTitle: landing.successTitle ?? landing.SuccessTitle,
    successBody: landing.successBody ?? landing.SuccessBody,
    successClosing: landing.successClosing ?? landing.SuccessClosing
  }
}

function interpolateCopy(value, { firstName, hotelName }) {
  return (value || '')
    .replace(/\{first\}/g, firstName || 'Joe')
    .replace(/\{hotel\}/g, hotelName || 'the hotel')
}

function PreviewField({ label, children }) {
  return (
    <div className={styles.field}>
      <span className={styles.label}>{label}</span>
      {children}
    </div>
  )
}

PreviewField.propTypes = {
  label: PropTypes.string.isRequired,
  children: PropTypes.node.isRequired
}

function SavibookPreviewModal({ step, hotelName, firstName, landing, onClose }) {
  const [screen, setScreen] = useState(PREVIEW_SCREENS.form)

  const draftLanding = normalizeLanding(landing)
  const fallbackCopy = getSavibookPreviewCopy(step.id, { firstName, hotelName })
  const copy = draftLanding
    ? {
        header: interpolateCopy(draftLanding.header, { firstName, hotelName }),
        body: interpolateCopy(draftLanding.body, { firstName, hotelName }),
        cta: draftLanding.cta || fallbackCopy.cta,
        successTitle: interpolateCopy(draftLanding.successTitle, { firstName, hotelName }),
        successBody: interpolateCopy(draftLanding.successBody, { firstName, hotelName }),
        successClosing: interpolateCopy(draftLanding.successClosing, { firstName, hotelName })
      }
    : fallbackCopy

  useEffect(() => {
    function handleKeyDown(event) {
      if (event.key === 'Escape') {
        onClose()
      }
    }

    document.addEventListener('keydown', handleKeyDown)

    return () => document.removeEventListener('keydown', handleKeyDown)
  }, [onClose])

  return (
    <Dialog>
      <div
        className={styles.shell}
        role="dialog"
        aria-modal="true"
        aria-label={`Landing Page Preview — ${step.title}`}
        data-testid="landing-page-preview-modal"
      >
        <div className={styles.topBar}>
          <div className={styles.topTitles}>
            <span className={styles.topEyebrow}>Landing Page Preview</span>
            <h2 className={styles.topTitle}>{step.title}</h2>
          </div>
          <div className={styles.topActions}>
            <div className={styles.stateToggle} role="group" aria-label="Preview state">
              <button
                type="button"
                className={[
                  styles.stateButton,
                  screen === PREVIEW_SCREENS.form ? styles.stateButtonActive : ''
                ].join(' ')}
                onClick={() => setScreen(PREVIEW_SCREENS.form)}
              >
                Landing
              </button>
              <button
                type="button"
                className={[
                  styles.stateButton,
                  screen === PREVIEW_SCREENS.success ? styles.stateButtonActive : ''
                ].join(' ')}
                onClick={() => setScreen(PREVIEW_SCREENS.success)}
              >
                Success
              </button>
            </div>
            <button
              type="button"
              className={styles.closeButton}
              onClick={onClose}
              aria-label="Close preview"
            >
              ×
            </button>
          </div>
        </div>

        <div className={styles.stage}>
          <div className={styles.phone}>
            <div className={styles.page}>
              <div
                className={styles.hero}
                style={{
                  backgroundImage: `linear-gradient(180deg, rgba(12, 24, 48, 0.55) 0%, rgba(12, 24, 48, 0.75) 100%), url(${HERO_IMAGE})`
                }}
              >
                <div className={styles.brandRow}>
                  <div className={styles.hotelName}>{hotelName}</div>
                </div>

                {screen === PREVIEW_SCREENS.form && (
                  <>
                    <h1 className={styles.header}>{copy.header}</h1>
                    <p className={styles.body}>{copy.body}</p>
                  </>
                )}
              </div>

              <div className={styles.panel}>
                {screen === PREVIEW_SCREENS.form && (
                  <div className={styles.form}>
                    <p className={styles.manualHint}>
                      Please confirm your details below so we can get your stay ready.
                    </p>

                    <PreviewField label="Full name">
                      <div className={styles.input}>
                        <span className={styles.placeholder}>Full name</span>
                      </div>
                    </PreviewField>

                    <PreviewField label="Email address">
                      <div className={styles.input}>
                        <span className={styles.placeholder}>Email address</span>
                      </div>
                    </PreviewField>

                    <PreviewField label="Phone number">
                      <div className={styles.input}>
                        <span className={styles.placeholder}>( ___ ) ___ - ____</span>
                      </div>
                    </PreviewField>

                    <div className={styles.cta}>{copy.cta}</div>

                    <div className={styles.consent}>
                      <span className={styles.consentBox} aria-hidden="true" />
                      <span className={styles.consentLabel}>
                        Keep me posted with offers and updates
                        {hotelName ? ` from ${hotelName}` : ''}. You can unsubscribe at any
                        time.
                      </span>
                    </div>

                    <p className={styles.legalText}>
                      By continuing you consent to the hotel contacting you about your
                      stay using the details you provide.
                    </p>
                  </div>
                )}

                {screen === PREVIEW_SCREENS.success && (
                  <div className={styles.success}>
                    <div className={styles.successIcon} aria-hidden="true">
                      ✓
                    </div>
                    <h2 className={styles.successTitle}>{copy.successTitle}</h2>
                    <p className={styles.successBody}>{copy.successBody}</p>
                    {copy.successClosing && (
                      <p className={styles.successClosing}>{copy.successClosing}</p>
                    )}
                  </div>
                )}
              </div>
            </div>
          </div>
        </div>

        <p className={styles.footNote}>
          Visual preview only — nothing is sent and no guest data is captured.
        </p>
      </div>
    </Dialog>
  )
}

SavibookPreviewModal.propTypes = {
  step: PropTypes.shape({
    id: PropTypes.string.isRequired,
    title: PropTypes.string.isRequired
  }).isRequired,
  hotelName: PropTypes.string,
  firstName: PropTypes.string,
  landing: PropTypes.object,
  onClose: PropTypes.func.isRequired
}

export default SavibookPreviewModal
